// React port of PROTO/src/banner.js — the clan's torn brush-stroke banner,
// rendered in WebGL. Shaders are copied verbatim from the prototype.
//
// kind:
//   cloth — the black banner as fabric, folds rolling along it.
//   cave  — plain black ground, its top the banner's torn edge, rippling
//           (the cave's background and the footer).
//   sea   — the same torn shape filled with Caribbean water.
//   video — a muted looping clip inside the torn banner.
//   frame — the inverse: paints `tint` (the page ground) everywhere OUTSIDE the
//           waving torn shape, so whatever sits underneath (a YouTube embed)
//           shows through as a waving flag. Place it above that content.
//
// This component renders only `<canvas class="banner-gl">`. The caller must
// place it as the FIRST child of the host element the banner should fill
// (the host supplies position/size via CSS — see .banner-gl / .banner-foot /
// html.dark main in site.css). On success the host gets `banner-ready`; on
// WebGL failure (or link failure) it gets `no-gl` so CSS can fall back to a
// static image.
//
// All mounted banners share one requestAnimationFrame loop and one "wind"
// value driven by scroll speed (a module-level singleton), exactly as the
// prototype's single IIFE did for every host on the page. The loop starts
// when the first banner mounts and stops when the last one unmounts.
import { useEffect, useRef } from 'react';
import type { JSX } from 'react';

export type BannerKind = 'cloth' | 'cave' | 'sea' | 'video' | 'frame';

export interface BannerCanvasProps {
  kind: BannerKind;
  tint?: string;
  videoSrc?: string;
}

const BANNER_SRC = '/art/banner.webp';
const SEA_SRC = '/art/sea.jpg';

const VS = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

const COMMON = [
  'precision mediump float;uniform sampler2D tex;uniform vec2 res;uniform float t;uniform float wind;uniform vec4 tint;',
  // narrow screens: fewer, lower folds, so the edge waves like cloth instead of squeezing into steep spikes
  'float FQ=1.0, AM=1.0;',
  'void fit(float asp){ FQ=clamp(asp/2.2,0.35,1.0); AM=clamp(asp/1.6,0.25,1.0); }',
  'vec2 edgeWarp(vec2 uv,float A,float ph){',
  '  float fold=sin(ph)+0.45*sin(uv.x*FQ*13.0-t*2.1+1.7)+0.2*sin(uv.x*FQ*27.0-t*3.3);',
  '  vec2 s=vec2(uv.x+0.004*A*sin(uv.y*5.0+ph), uv.y+0.035*A*AM*fold*(0.6+0.4*sin(uv.x*2.0)));',
  '  s.y=(s.y-0.5)*1.06+0.5; s.x=(s.x-0.5)*AM+0.5; return s; }',
].join('\n');

const FS: Record<BannerKind, string> = {
  frame:
    COMMON +
    [
      'void main(){ vec2 uv=gl_FragCoord.xy/res; uv.y=1.0-uv.y;',
      '  fit(res.x/res.y); float A=0.5+0.9*wind, ph=uv.x*FQ*6.0-t*1.3; vec2 s=edgeWarp(uv,A,ph);',
      '  float m=(s.y<0.0||s.y>1.0)?0.0:texture2D(tex,s).a;',
      '  gl_FragColor=vec4(tint.rgb*(1.0-m),1.0-m); }',
    ].join('\n'),
  cloth:
    COMMON +
    [
      'void main(){ vec2 uv=gl_FragCoord.xy/res; uv.y=1.0-uv.y;',
      '  fit(res.x/res.y); float A=0.5+0.9*wind, ph=uv.x*FQ*6.0-t*1.3; vec2 s=edgeWarp(uv,A,ph);',
      '  if(s.y<0.0||s.y>1.0){gl_FragColor=vec4(0.0);return;}',
      '  vec4 c=texture2D(tex,s);',
      '  if(tint.a>0.0){ float br=dot(c.rgb,vec3(0.333))/max(c.a,0.01);',
      '    float gr=fract(sin(dot(floor(gl_FragCoord.xy),vec2(12.9898,78.233)))*43758.5453);',
      '    c.rgb=tint.rgb*(0.97+(br-0.12)*4.0+0.03*gr)*c.a; }',
      '  float slope=cos(ph)+0.45*cos(uv.x*FQ*13.0-t*2.1+1.7)*2.1;',
      '  float L=tint.a>0.0?0.045:0.16;',
      '  gl_FragColor=vec4(c.rgb*(1.0+L*A*slope),c.a); }',
    ].join('\n'),
  cave:
    COMMON +
    [
      'uniform float edge;',
      'void main(){ vec2 uv=gl_FragCoord.xy/res; float py=res.y-gl_FragCoord.y;',
      '  fit(res.x/(2.0*edge)); float A=0.5+0.9*wind, ph=uv.x*FQ*6.0-t*1.3, a=1.0;',
      '  if(py<edge){ vec2 s=edgeWarp(vec2(uv.x,0.5*py/edge),A,ph); a=s.y<0.0?0.0:texture2D(tex,vec2(s.x,min(s.y,0.5))).a; }',
      '  float slope=cos(ph)+0.45*cos(uv.x*FQ*13.0-t*2.1+1.7)*2.1;',
      '  float lit=1.0+0.16*A*slope*(1.0-smoothstep(0.0,edge*1.4,py));',
      '  gl_FragColor=vec4(vec3(0.129,0.125,0.10)*lit*a,a); }',
    ].join('\n'),
  video:
    COMMON +
    [
      'uniform sampler2D vid;uniform vec2 vres;uniform float vready;',
      'float hh(vec2 p){ return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453); }',
      'void main(){ vec2 uv=gl_FragCoord.xy/res; uv.y=1.0-uv.y;',
      '  fit(res.x/res.y); float A=0.5+0.9*wind, ph=uv.x*FQ*6.0-t*1.3; vec2 s=edgeWarp(uv,A,ph);',
      '  if(s.y<0.0||s.y>1.0){gl_FragColor=vec4(0.0);return;}',
      '  vec4 c=texture2D(tex,s);',
      '  vec2 q=s-0.5; float ca=res.x/res.y, va=vres.x/max(vres.y,1.0);',
      '  if(ca>va) q.y*=va/ca; else q.x*=ca/va;',
      '  vec3 v=texture2D(vid,q+0.5).rgb;',
      '  float g=dot(v,vec3(0.3,0.5,0.2)); v=mix(vec3(g),v,0.8)*0.62;',
      '  v*=0.95+0.1*hh(floor(gl_FragCoord.xy)+floor(t*20.0));',
      '  vec3 col=mix(c.rgb, v*c.a, vready);',
      '  float slope=cos(ph)+0.45*cos(uv.x*FQ*13.0-t*2.1+1.7)*2.1;',
      '  gl_FragColor=vec4(col*(1.0+0.12*A*slope),c.a); }',
    ].join('\n'),
  sea:
    COMMON +
    [
      'uniform sampler2D water;uniform float storm;uniform float flow;',
      'float hh(vec2 p){ return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453); }',
      'float vn(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f);',
      '  return mix(mix(hh(i),hh(i+vec2(1,0)),f.x), mix(hh(i+vec2(0,1)),hh(i+vec2(1,1)),f.x), f.y); }',
      'float fbm(vec2 p){ float s=0.0, a=0.5; for(int k=0;k<4;k++){ s+=a*vn(p); p=p*2.1+vec2(3.1,7.7); a*=0.5; } return s; }',
      'void main(){ vec2 uv=gl_FragCoord.xy/res; uv.y=1.0-uv.y;',
      '  vec2 w=vec2(uv.x*res.x/1024.0, uv.y*res.y/349.0);',
      '  float wx=(w.x+t*0.012)*6.2831;',
      // a strong swell: the ocean strip on profiles is shallow, so the edges need height to read as waves
      // in a storm (0..1) a long, heavy swell rolls through and the surface turns a little choppy;
      // the waves grow, they never speed up (that only makes them wobble); the mean drops a little
      // so the crests stay in the strip
      '  float top=0.125+0.05*storm+0.042*sin(wx*0.7+0.3)+0.016*sin(wx*1.6+1.0)',
      '    +0.036*sin(wx*1.15-t*0.7)+0.014*sin(wx*2.4+t*0.9+1.3)',
      '    +storm*(0.045*sin(wx*0.42-t*0.55+0.8)+0.008*sin(wx*3.3-t*1.2));',
      '  float bot=0.875+0.042*sin(wx*0.65+2.0)+0.016*sin(wx*1.45)',
      '    +0.036*sin(wx*1.1-t*0.65+2.1)+0.014*sin(wx*2.3+t*0.85+0.4);',
      '  float px=1.2/res.y;',
      '  float m=smoothstep(top-px,top+px,uv.y)*(1.0-smoothstep(bot-px,bot+px,uv.y));',
      '  if(m<=0.0){gl_FragColor=vec4(0.0);return;}',
      // the currents (texture drift, ripples, light) run on their own clock, faster in a storm
      '  w+=vec2(flow*0.012, 0.0);',
      '  w+=0.006*vec2(sin(w.y*18.0+flow*0.9), sin(w.x*14.0-flow*0.7));',
      '  vec3 c=texture2D(water,w).rgb;',
      '  float l=dot(c,vec3(0.3,0.45,0.25));',
      '  vec3 sea=mix(vec3(0.02,0.30,0.38), vec3(0.20,0.62,0.68), smoothstep(0.08,0.55,l));',
      '  float asp=res.x/res.y; vec2 g=vec2(uv.x*asp-(0.35*asp+0.25*asp*sin(t*0.05)), uv.y-0.25);',
      '  float glint=exp(-dot(g,g)*1.4);',
      '  sea+=glint*(0.10+0.9*pow(smoothstep(0.25,0.75,l),2.0))*vec3(0.75,0.92,0.95);',
      '  c=sea*(0.95+0.1*sin(w.x*6.0+w.y*3.0-flow*0.6));',
      '  float down=uv.y+0.12*(fbm(vec2(uv.x*res.x/res.y*1.2+t*0.01,uv.y*2.0))-0.5);',
      '  float deep=smoothstep(0.0,1.05,down);',
      '  vec3 navy=vec3(0.035,0.06,0.17)*(0.6+1.5*l);',
      '  c=mix(c, navy, deep*0.85);',
      '  float gray=dot(c,vec3(0.3,0.5,0.2)); c=mix(vec3(gray),c,0.82);',
      '  c=mix(c, c*vec3(0.58,0.64,0.68)+vec3(0.03), storm*0.65);',
      '  c*=0.95+0.1*hh(floor(gl_FragCoord.xy)+floor(t*20.0));',
      '  gl_FragColor=vec4(c,1.0)*m; }',
    ].join('\n'),
};

interface UniformMap {
  tex: WebGLUniformLocation | null;
  res: WebGLUniformLocation | null;
  t: WebGLUniformLocation | null;
  wind: WebGLUniformLocation | null;
  water: WebGLUniformLocation | null;
  tint: WebGLUniformLocation | null;
  edge: WebGLUniformLocation | null;
  vid: WebGLUniformLocation | null;
  vres: WebGLUniformLocation | null;
  vready: WebGLUniformLocation | null;
  storm: WebGLUniformLocation | null;
  flow: WebGLUniformLocation | null;
}

interface BannerItem {
  host: HTMLElement;
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  U: UniformMap;
  ready: boolean;
  visible: boolean;
  t: number;
  /** Extra time on the sea's currents, gathered while a storm blows. */
  flow: number;
  pace: number;
  video?: HTMLVideoElement;
  vok?: boolean;
  grab?: () => void;
  size: () => void;
}

// ---- module-level singleton: one rAF loop + one wind value for every
// mounted banner, exactly as the prototype's single IIFE drove every host on
// the page. Starts on first mount, stops on last unmount. ----

const items: BannerItem[] = [];
let refCount = 0;
let rafId: number | null = null;
let lastFrameTime = 0;
let wind = 0.25;
/** How stormy the sea is (0 calm .. 1 storm), eased towards stormTarget. */
let storm = 0;
let stormTarget = 0;

/** Raises (1) or settles (0) a storm on every sea banner; it eases in and out. */
export function setSeaStorm(level: number): void {
  stormTarget = Math.max(0, Math.min(1, level));
}
let lastScrollY = 0;

let reducedMotionQuery: MediaQueryList | null = null;
let reduceMotion = false;

function getReduceMotion(): boolean {
  if (!reducedMotionQuery) {
    reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotion = reducedMotionQuery.matches;
  }
  return reduceMotion;
}

function handleReducedMotionChange(e: MediaQueryListEvent): void {
  reduceMotion = e.matches;
}

function handleResize(): void {
  for (const item of items) item.size();
}

function frame(now: number): void {
  rafId = requestAnimationFrame(frame);
  const dt = Math.min((now - lastFrameTime) / 1000, 0.05);
  lastFrameTime = now;
  const scrollY = window.scrollY;
  const dy = Math.abs(scrollY - lastScrollY);
  lastScrollY = scrollY;
  const reduce = getReduceMotion();
  if (!reduce) {
    const target = Math.max(0.2, Math.min(1, dy / 20));
    wind += (target - wind) * (target > wind ? 0.15 : 0.02);
  }
  storm += (stormTarget - storm) * Math.min(1, dt * (stormTarget > storm ? 0.7 : 0.45));
  for (const item of items) {
    if (!reduce) item.t += dt * item.pace * (0.8 + 1.6 * wind);
    // in a storm the water's currents run faster (the waves' shape keeps its pace): their own clock
    if (!reduce) item.flow += dt * item.pace * (0.8 + 1.6 * wind) * storm * 1.6;
    if (!item.ready || !item.visible) continue;
    const gl = item.gl;
    if (item.video && !item.video.paused && item.grab) item.grab();
    gl.uniform1f(item.U.t, item.t);
    gl.uniform1f(item.U.wind, reduce ? 0 : wind);
    gl.uniform1f(item.U.storm, reduce ? 0 : storm);
    gl.uniform1f(item.U.flow, item.t + item.flow);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

// A canvas also changes size without the window resizing — the cave's
// grows as its page loads — and a drawing buffer sized at mount would then
// be stretched across the new box. So every canvas is re-measured whenever
// its own box changes.
let canvasObserver: ResizeObserver | null = null;

function observeCanvas(item: BannerItem): void {
  if (typeof ResizeObserver === 'undefined') return;
  canvasObserver ??= new ResizeObserver((entries) => {
    for (const entry of entries) items.find((i) => i.canvas === entry.target)?.size();
  });
  canvasObserver.observe(item.canvas);
}

function registerItem(item: BannerItem): void {
  items.push(item);
  observeCanvas(item);
  refCount += 1;
  if (refCount === 1) {
    lastScrollY = window.scrollY;
    window.addEventListener('resize', handleResize);
    getReduceMotion();
    reducedMotionQuery?.addEventListener('change', handleReducedMotionChange);
  }
  if (rafId === null) {
    lastFrameTime = performance.now();
    rafId = requestAnimationFrame(frame);
  }
}

function unregisterItem(item: BannerItem): void {
  const idx = items.indexOf(item);
  if (idx !== -1) items.splice(idx, 1);
  canvasObserver?.unobserve(item.canvas);
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) {
    window.removeEventListener('resize', handleResize);
    reducedMotionQuery?.removeEventListener('change', handleReducedMotionChange);
  }
  if (items.length === 0 && rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

// ---- shared texture cache: the prototype loads art/banner.webp once and
// reuses the same <img> across every host's own WebGL context. ----

const imageCache = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  let p = imageCache.get(src);
  if (!p) {
    p = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`failed to load ${src}`));
      img.src = src;
    });
    imageCache.set(src, p);
  }
  return p;
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  return s;
}

function parseTint(tint: string | undefined): [number, number, number, number] {
  const hex = /^#?([0-9a-f]{6})$/i.exec(tint ?? '');
  const n = hex ? parseInt(hex[1] ?? '0', 16) : 0;
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, hex ? 1 : 0];
}

// React 19's <StrictMode> runs every effect's setup -> cleanup -> setup
// synchronously in dev, reusing the SAME <canvas> DOM node (only effects are
// replayed, not the render). A WebGL context, once explicitly lost via
// WEBGL_lose_context, can never be reacquired on that canvas — so losing it
// eagerly in cleanup would make the StrictMode remount permanently fall back
// to `no-gl`. Deferring the loss by one tick and cancelling it if setup
// claims the same canvas again (as StrictMode does, synchronously, before
// the timeout fires) keeps a genuine unmount's cleanup real while surviving
// the dev-only double-invoke.
const pendingContextLoss = new WeakMap<HTMLCanvasElement, ReturnType<typeof setTimeout>>();

export function BannerCanvas(props: BannerCanvasProps): JSX.Element {
  const { kind, tint, videoSrc } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const hostOrNull = canvas?.parentElement;
    if (!canvas || !hostOrNull) return undefined;
    const host: HTMLElement = hostOrNull;

    const pendingLoss = pendingContextLoss.get(canvas);
    if (pendingLoss !== undefined) {
      clearTimeout(pendingLoss);
      pendingContextLoss.delete(canvas);
    }

    const glOrNull = canvas.getContext('webgl', { premultipliedAlpha: true });
    if (!glOrNull) {
      host.classList.add('no-gl');
      return () => host.classList.remove('no-gl');
    }
    // A fresh, non-nullable binding: TypeScript does not retain narrowing of
    // an outer `const` inside nested function declarations (startVideo,
    // upload, the IntersectionObserver callback, the cleanup closure below),
    // since those may run after this scope returns.
    const gl: WebGLRenderingContext = glOrNull;

    const vs = compileShader(gl, gl.VERTEX_SHADER, VS);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FS[kind]);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) {
      host.classList.add('no-gl');
      return () => host.classList.remove('no-gl');
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      host.classList.add('no-gl');
      return () => host.classList.remove('no-gl');
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const U: UniformMap = {
      tex: gl.getUniformLocation(prog, 'tex'),
      res: gl.getUniformLocation(prog, 'res'),
      t: gl.getUniformLocation(prog, 't'),
      wind: gl.getUniformLocation(prog, 'wind'),
      water: gl.getUniformLocation(prog, 'water'),
      tint: gl.getUniformLocation(prog, 'tint'),
      edge: gl.getUniformLocation(prog, 'edge'),
      vid: gl.getUniformLocation(prog, 'vid'),
      vres: gl.getUniformLocation(prog, 'vres'),
      vready: gl.getUniformLocation(prog, 'vready'),
      storm: gl.getUniformLocation(prog, 'storm'),
      flow: gl.getUniformLocation(prog, 'flow'),
    };

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform4f(U.tint, ...parseTint(tint));

    const item: BannerItem = {
      host,
      canvas,
      gl,
      U,
      ready: false,
      visible: true,
      t: 10 + Math.random() * 90,
      flow: 0,
      pace: 0.8 + Math.random() * 0.4,
      size: () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const r = canvas.getBoundingClientRect();
        canvas.width = Math.round(r.width * dpr);
        canvas.height = Math.round(r.height * dpr);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(U.res, canvas.width, canvas.height);
        gl.uniform1f(U.edge, 240 * dpr);
      },
    };

    let cancelled = false;
    let videoEl: HTMLVideoElement | undefined;
    let onVideoLoadedData: (() => void) | undefined;

    function startVideo(): void {
      if (!videoSrc) return;
      const v = document.createElement('video');
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.preload = 'auto';
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      const vt = gl.createTexture();
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, vt);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform1i(U.vid, 1);
      gl.uniform1f(U.vready, 0);
      gl.activeTexture(gl.TEXTURE0);
      item.grab = () => {
        if (v.readyState < 2) return;
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, vt);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
        gl.activeTexture(gl.TEXTURE0);
        if (!item.vok) {
          item.vok = true;
          gl.uniform2f(U.vres, v.videoWidth, v.videoHeight);
          gl.uniform1f(U.vready, 1);
        }
      };
      item.video = v;
      onVideoLoadedData = () => item.grab?.();
      v.addEventListener('loadeddata', onVideoLoadedData);
      v.src = videoSrc;
      if (!getReduceMotion()) {
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
      videoEl = v;
    }

    function upload(img: HTMLImageElement): void {
      if (cancelled) return;
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform1i(U.tex, 0);
      if (kind === 'video') startVideo();
      if (kind !== 'sea') {
        item.ready = true;
        host.classList.add('banner-ready');
        return;
      }
      loadImage(SEA_SRC)
        .then((wimg) => {
          if (cancelled) return;
          const wt = gl.createTexture();
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, wt);
          gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, wimg);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
          gl.uniform1i(U.water, 1);
          gl.activeTexture(gl.TEXTURE0);
          item.ready = true;
          host.classList.add('banner-ready');
        })
        .catch(() => {});
    }

    item.size();
    loadImage(BANNER_SRC)
      .then((img) => upload(img))
      .catch(() => {});

    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      item.visible = entry.isIntersecting;
      if (item.video && !getReduceMotion()) {
        if (item.visible) item.video.play().catch(() => {});
        else item.video.pause();
      }
    });
    io.observe(host);

    registerItem(item);

    return () => {
      cancelled = true;
      io.disconnect();
      unregisterItem(item);
      if (videoEl) {
        if (onVideoLoadedData) videoEl.removeEventListener('loadeddata', onVideoLoadedData);
        videoEl.pause();
        videoEl.removeAttribute('src');
        videoEl.load();
      }
      host.classList.remove('no-gl', 'banner-ready');
      const timeoutId = setTimeout(() => {
        pendingContextLoss.delete(canvas);
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      }, 0);
      pendingContextLoss.set(canvas, timeoutId);
    };
  }, [kind, tint, videoSrc]);

  return <canvas ref={canvasRef} className="banner-gl" aria-hidden="true" />;
}
